// Onda 4 — Email → Reunião
// Converte um email (linha de emails_gmail conforme exposta por useEmailsHub)
// em um payload de prefill consumido pelo EventCreateModal.
//
// 100% síncrono e puro: a busca por contatos externos (enriquecimento) é
// responsabilidade do modal, para manter este helper testável.

const TEAM_EMAIL_TO_COLAB = {
  'eduardo@astoncapital.com.br':  'eduardo',
  'caroline@astoncapital.com.br': 'caroline',
  'fabio@spareas.com.br':         'fabio',
};

const NOISE_EMAIL_FRAGMENTS = ['noreply', 'no-reply', 'mailer-daemon', 'postmaster@'];

function cleanSubject(subject) {
  if (!subject) return 'Reunião';
  const stripped = subject.replace(/^(\s*(Re|RE|Fwd|FWD|Fw|FW|Enc|ENC|Res|RES):\s*)+/i, '').trim();
  return stripped || 'Reunião';
}

function isNoise(email) {
  if (!email) return true;
  const lower = email.toLowerCase();
  return NOISE_EMAIL_FRAGMENTS.some((frag) => lower.includes(frag));
}

export function getNextBusinessSlot(now = new Date()) {
  const next = new Date(now);
  if (next.getHours() < 17) {
    next.setHours(next.getHours() + 1, 0, 0, 0);
  } else {
    next.setDate(next.getDate() + 1);
    next.setHours(10, 0, 0, 0);
  }
  // Pular fim de semana
  while (next.getDay() === 0 || next.getDay() === 6) {
    next.setDate(next.getDate() + 1);
    next.setHours(10, 0, 0, 0);
  }
  return next;
}

function getEndSlot(start, hours = 1) {
  const end = new Date(start);
  end.setHours(end.getHours() + hours);
  return end;
}

/**
 * Monta o prefill de um EventCreateModal a partir de um email.
 *
 * @param {Object} email - linha de emails_gmail (com from_email, to_emails, cc_emails, subject, empresa_id, oferta_id, area_mencionada_id, empresa_proprietaria_id, id).
 * @returns {Object} prefill - { title, description, eventType, priority, startAt, endAt, allDay, attendees, links, source }
 */
export function buildEventFromEmail(email) {
  if (!email) return null;

  const title = cleanSubject(email.subject);

  // Participantes: from + to + cc, dedup case-insensitive, removendo ruído
  const rawEmails = [
    email.from_email,
    ...(Array.isArray(email.to_emails) ? email.to_emails : []),
    ...(Array.isArray(email.cc_emails) ? email.cc_emails : []),
  ];
  const seen = new Set();
  const unique = [];
  for (const e of rawEmails) {
    if (!e) continue;
    const lower = String(e).trim().toLowerCase();
    if (!lower || isNoise(lower) || seen.has(lower)) continue;
    seen.add(lower);
    unique.push(lower);
  }

  const teamMembers = [];
  const externals = [];
  for (const e of unique) {
    const colab = TEAM_EMAIL_TO_COLAB[e];
    if (colab) {
      teamMembers.push({ type: 'internal', colaboradorId: colab, email: e });
    } else {
      externals.push({ type: 'external', email: e, name: null, contatoId: null });
    }
  }

  // Links: entidades vinculadas ao email + o próprio email como origem
  const links = [];
  const pushLink = (entityType, entityId, relationType = 'origin') => {
    if (!entityId) return;
    // dedup por (entityType, entityId)
    if (links.some((l) => l.entityType === entityType && l.entityId === entityId)) return;
    links.push({ entityType, entityId, relationType });
  };
  pushLink('empresa', email.empresa_id, 'origin');
  pushLink('empresa', email.empresa_proprietaria_id, 'related');
  pushLink('oferta', email.oferta_id, 'origin');
  pushLink('area', email.area_mencionada_id, 'related');
  if (email.id) pushLink('email_thread', email.id, 'origin');

  // Dono do evento: primeiro membro da equipe presente; fallback para currentUser pelo modal
  const ownerColaboradorId = teamMembers.length > 0 ? teamMembers[0].colaboradorId : null;

  const start = getNextBusinessSlot();
  const end = getEndSlot(start, 1);

  return {
    title,
    description: `Reunião agendada a partir do email: "${email.subject || '(sem assunto)'}"`,
    eventType: 'meeting',
    priority: 'medium',
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    allDay: false,
    ownerColaboradorId,
    attendees: [...teamMembers, ...externals],
    links,
    source: 'gmail_thread',
    // Lista crua de emails externos para o modal usar no enriquecimento via contatos
    externalEmails: externals.map((a) => a.email),
  };
}

export default buildEventFromEmail;
